import { RxCollection, RxDocument } from 'rxdb'
import { Subject, from, of } from 'rxjs'
import { mergeMap } from 'rxjs/operators'
import { LoadingService } from '@cicero/data/utils'
import { Store } from './Store'
import { BaseSchema, BaseSchemaCreateParams, DELETE_DOC, EntityNotFoundError, GENERATE_SYNCED_RAW, GET_BY_ID, IModel, IQuery } from '../helpers'

/**
 * A service that provides the required methiods to manipulate a model locally
 * but also trigger jobs to fetch/sync changed data from/with the API when online
 */
export class ModelService<T extends BaseSchema> {
  loading$ = new LoadingService(this.schema.NAME)
  active$ = new Subject<RxDocument<T>|null>()
  stats = {
    creations: 0,
    deletions: 0,
    parsed_children: 0,
    queries: 0,
    upserts: 0,
    persists: 0,
  }

  constructor(public schema: IModel<T>, public store: Store) {
    // LOGGER.debug('[MODEL_INITIALIZE]', schema.NAME, { schema, service: this })
  }

  async GET_COLLECTION(): Promise<RxCollection<T>> {
    return this.store.getCollection(this.schema)
  }

  /**
   * Create a doc with some params, but don't save it yet
   * @param params 
   */
  async NEW(params: Partial<T>) {
    const c = await this.GET_COLLECTION()
    this.stats.creations++
    return c.newDocument(params)
  }

  /**
   * Create a doc and save it. It will sync in the background
   * @param params 
   */
  async CREATE(params: BaseSchemaCreateParams<T>) {
    const c = await this.GET_COLLECTION()
    const doc = await c.insert(params as any)
    if (this.schema.LIFECYCLE.afterCreate) { await this.schema.LIFECYCLE.afterCreate(this.store, doc) }
    this.stats.creations++
    return doc
  }

  /**
   * Create multiple docs and save them. They will sync in the background
   * @param params 
   */
  async CREATE_BULK(params: BaseSchemaCreateParams<T>[]) {
    const c = await this.GET_COLLECTION()
    const docs = await c.bulkInsert(params as any)
    if (this.schema.LIFECYCLE.afterCreate) {
      // await Promise.all(docs.map(doc => this.schema.LIFECYCLE.afterCreate(this.store, doc))
    }
    this.stats.creations += docs.success.length
    return docs
  }

  /**
   * Run a query for 1 item. Also triggers a fetch job unless recently cached
   * @param query A valid query
   */
  protected async FIND_ONE(query: IQuery<T>) {
    const { cacheKey, cachePeriod, fetchDispatch } = this.schema.LIFECYCLE
    this.stats.queries++
    this.store.cache.performUnlessCached(cacheKey(this.schema, query), () => fetchDispatch(this.store, this, query), cachePeriod(this.schema, query))

    const c = await this.GET_COLLECTION()
    return c.findOne(this.schema.LIFECYCLE.transformQuery(query))
  }

  /**
   * Run a query for multiple items. Also triggers a fetchQuery job unless recently cached
   * @param query A valid query
   */
  protected async FIND(query: IQuery<T>) {
    const { cacheKey, cachePeriod, fetchQueryDispatch } = this.schema.LIFECYCLE
    this.stats.queries++
    this.store.cache.performUnlessCached(cacheKey(this.schema, query), () => fetchQueryDispatch(this.store, this, query), cachePeriod(this.schema, query))

    const c = await this.GET_COLLECTION()
    return c.find(this.schema.LIFECYCLE.transformQuery(query))
  }

  async GET_BY_KEY_VALUE(key: string, value: any, extraQuery?: Partial<IQuery<T>>) {
    return (await this.FIND_ONE({ ...extraQuery, selector: { ...extraQuery?.selector, [key]: value } })).exec()
  }

  /**
   * Gets a doc by an ID. If the ID is not found and is a uuid, will also check meta.uuid
   * @param id a valid offline or online id
   */
  async GET(id: string) {
    const { cacheKey, cachePeriod, fetchDispatch } = this.schema.LIFECYCLE
    const query: IQuery<T> = { selector: { id } }
    this.stats.queries++
    this.store.cache.performUnlessCached(cacheKey(this.schema, query), () => fetchDispatch(this.store, this, query), cachePeriod(this.schema, query))
    return GET_BY_ID(await this.GET_COLLECTION(), id)
  }

  async QUERY(query: IQuery<T>) {
    return (await this.FIND(query)).exec()
  }

  GET$(id: string) {
    return from(this.GET(id)).pipe(mergeMap(q => q ? q.$ : of(null)))
  }

  GET_BY_KEY_VALUE$(key: string, value: any, extraQuery: Partial<IQuery<T>> = {}) {
    return from(this.FIND_ONE({ ...extraQuery, selector: { ...extraQuery?.selector, [key]: value } })).pipe(mergeMap(q => q.$))
  }

  QUERY$(query: IQuery<T> = { selector: {} }) {
    return from(this.FIND(query)).pipe(mergeMap(q => q.$))
  }

  /**
   * Recursively parses and stores associations for this model, and attached their foreign key if defined
   * @param entity The object possibly matching your schema. Will be modified.
   */
  protected async PARSE_ASSOCIATIONS(entity: any) {
    const { associations } = this.schema.CONFIG
    if (associations) {
      let parsed = 0
      const assocs = associations()
      for (const { key, model, foreignKey } of assocs) {
        if (key && entity[key]) {
          const child = this.store.getModel(model as any)
          const plural = Array.isArray(entity[key]) // TODO Should use type here too? What if inconsistent
          const entities = plural ? entity[key] : [entity[key]]
          const results: any[] = await Promise.all(entities.map(async (e: any) => child.PERSIST(e)))
          parsed += entities.length
          if (foreignKey) {
            entity[foreignKey] = plural ? results.map((r: any) => r.id) : results[0]['id']
          }
          delete entity[key]
        }
      }
      return parsed
    } else {
      return 0
    }
  }

  /**
   * Persist a doc, usually from the API, to the store. It will not be synced until further changes occur
   * @param entity an entity to store
   * @param id an id, if it exists in the store, the entity will be merged into it
   */
  async PERSIST(entity: T, id?: string) {
    const c = await this.GET_COLLECTION()
    const original = JSON.parse(JSON.stringify(entity)) // eslint-disable-line @typescript-eslint/no-unused-vars
    const parsedChildren = await this.PARSE_ASSOCIATIONS(entity)
    const meta = GENERATE_SYNCED_RAW(entity)
    const { cacheKey, cachePeriod, merge } = this.schema.LIFECYCLE
    let docIfExists: RxDocument<T>|null = null

    if (this.schema.LIFECYCLE.sanitize) {
      entity = this.schema.LIFECYCLE.sanitize(this.schema, entity)
    }

    if (id) {
      docIfExists = await c.findOne(id).exec()
    } else if (entity.id) {
      docIfExists = await GET_BY_ID(c, entity.id)
      // TODO If need to change from old id to new id, check if doc is in use otherwise delete/create
    }

    if (docIfExists) {
      entity = merge(docIfExists.toJSON(), entity)
      const q = { selector: { id: entity.id } }
      const key = cacheKey(this.schema, q)
      const period = cachePeriod(this.schema, q)
      this.store.cache.addToCache(key, period)
    }

    entity.meta = { ...docIfExists?.meta, ...meta }
    const doc = await c.atomicUpsert(entity)
    this.stats.persists++
    this.stats.parsed_children += parsedChildren
    // LOGGER.debug('PERSIST', { parsedChildren, doc, id, entity, original })
    return doc
  }

  /**
   * A useful shorthand to update a doc
   * @param entity either a string id, an object with an id, or a valid rxdoc
   * @param update an object with keys to update, or a callback that modifies the doc
   */
  async UPDATE(entity: string|T|RxDocument<T>, update: Partial<T>|((d: T) => T)) {
    let doc: RxDocument<T>|null
    if (typeof entity === 'string') {
      doc = await this.GET(entity)
    } else if ('atomicUpdate' in entity) {
      doc = entity
    } else {
      doc = await this.GET(entity.id)
    }
    if (!doc) { throw new EntityNotFoundError('Cannot find doc to update', { entity }) }
    return doc.atomicUpdate(typeof update === 'function' ? update : (d) => ({ ...d, ...update }))
  }

  // /**
  //  * Shared function to perform the checks, merging, association parsing
  //  * and more for an entity about to be upserted
  //  * @param c The collection
  //  * @param entity the entity to check
  //  */
  // protected async PREPARE_TO_UPSERT(c: RxCollection, entity: T): Promise<{ entity: T, exists: boolean }> {
  //   const parsedChildren = await PARSE_ASSOCIATIONS(this.schema, entity, this.store)
  //   let exists = false
  //   this.stats.parsed_children += parsedChildren

  //   if (entity.id) {
  //     const docIfExists = IS_OFFLINE_ID(entity.id)
  //       ? await GET_BY_ID(c, entity.id)
  //       : await (await GET_BY_KEY_VALUE(c, 'meta.uuid', entity.id)).exec()

  //     if (docIfExists) {
  //       exists = true
  //       entity = this.schema.LIFECYCLE?.merge(docIfExists.toJSON(), entity) || entity
  //     }
  //   }

  //   this.stats.upserts++
  //   return { entity, exists }
  // }

  // /**
  //  * Single upsert
  //  * @param entity an individual entity to upsert
  //  * @param fresh set to true to signify this doc is fresh from the API, and changes won't be synced
  //  */
  // async UPSERT(entity: T): Promise<RxDocument<T>> {
  //   const c = await this.GET_COLLECTION()
  //   const prep = await this.PREPARE_TO_UPSERT(c, entity)
  //   return c.atomicUpsert(prep.entity)
  // }

  // /**
  //  * Bulk upsert, optimized to bulkCreate new items rather than individual upserts
  //  * Warning: does not guarantee entity return order, if you need to guarantee return
  //  * use look over the regular UPSERT operation
  //  * @param entities a list of entities to upsert
  //  * @param fresh set to true to signify this doc is fresh from the API, and changes won't be synced
  //  */
  // async UPSERT_BULK(entities: T[]): Promise<RxDocument<T>[]> {
  //   const c = await this.GET_COLLECTION()
  //   const prep = await Promise.all(entities.map(async e => this.PREPARE_TO_UPSERT(c, e)))
  //   const actions = prep.reduce((acc, p) => {
  //     if (p.exists) {
  //       acc.toUpdate.push(p.entity)
  //     } else {
  //       acc.toCreate.push(p.entity)
  //     }
  //     return acc
  //   }, { toCreate: [] as T[], toUpdate: [] as T[] })

  //   return Promise.all([
  //     actions.toCreate.length ? c.bulkInsert(actions.toCreate) : Promise.resolve(null),
  //     ...actions.toUpdate.map(async doc => c.atomicUpsert(doc)),
  //   ] as any).then(d => {
  //     if (d[0] === null) { return d.slice(1) }
  //     const { success, error } = d[0] as any
  //     if (error.length) { LOGGER.warn('ERR CREATING', error) }
  //     return success.concat(d.slice(1))
  //   })
  // }

  async DELETE(id: string|undefined|RxDocument<T>): Promise<RxDocument<T>> {
    this.stats.deletions++
    return DELETE_DOC(await this.GET_COLLECTION(), id)
  }
}
